import { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import "../../common/Common.css";
import { DataGrid } from "@mui/x-data-grid";
import "../InvoiceManagement/InvoiceManagement.css";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import {
  editPatientButton,
  printInvoiceButton,
} from "../../common/commonStyles";
import ReusableSnackbar from "../../shared/Alert/ReusableSnackbar";
import { roleFlags } from "../../shared/helper";
import { useSelector } from "react-redux";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ForwardToInboxOutlinedIcon from "@mui/icons-material/ForwardToInboxOutlined";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import { Tooltip } from "@mui/material";
import ConfirmDialog from "../../shared/ConfirmDialog/ConfirmDialog";
import IconButton from "@mui/material/IconButton";

import api from "../../services/api";

export default function PatientTable(props) {
  const { rows, getInvoiceDetails } = props;
  const [searchQuery, setSearchQuery] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState({
    status: false,
    message: "",
    severity: "",
  });
  const [paymentMode, setPaymentMode] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [open, setOpen] = useState(false);
  const handleOpenDialog = () => setOpen(true);
  const handleSnackClose = () => setSnackbarOpen(false);
  const handleCloseDialog = () => setOpen(false);
  const userDetail = useSelector((state) => state.userDetails.detail);
  const flags = roleFlags(userDetail?.role);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const APP_URL = process.env.REACT_APP_URL;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // months are 0-based
    const year = String(date.getFullYear()).slice(-2); // last two digits
    return `${day}-${month}-${year}`;
  };

  const handleChange = async (event, slug) => {
    setPaymentMode(event.target.value);
    try {
      let updatePaymentMode = await api.patch(
        `app/invoice_management/invoice/${slug}/payment`,
        {
          payment_mode: event.target.value,
        },
      );
      if (updatePaymentMode?.status === 200) {
        setSnackbarOpen({
          status: true,
          message: "Payment Mode Updated Successfully!",
        });
        getInvoiceDetails();
      }
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  const columns = [
    {
      field: "patient_name",
      headerName: "Name",
      minWidth: 120,
      editable: false,
      flex: 1,
    },
    {
      field: "yearMonth",
      headerName: "Period",
      minWidth: 100,
      editable: false,
      flex: 1,
      renderCell: (params) => {
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        const month = monthNames[(params.row.month ?? 1) - 1];
        const year = params.row.year ?? "";

        return <span>{`${month} ${year}`}</span>;
      },
    },
    {
      field: "published_at",
      headerName: "Date",
      minWidth: 100,
      editable: false,
      flex: 1,
      renderCell: (params) => {
        return <span>{formatDate(params.row.published_at)}</span>;
      },
    },

    {
      field: "planned_sessions",
      headerName: "Planned",
      minWidth: 10,
      editable: false,
      flex: 1,
    },
    {
      field: "absent_sessions",
      headerName: "Absent",
      minWidth: 10,
      editable: false,
      flex: 1,
    },
    {
      field: "billable_sessions",
      headerName: " Billable",
      minWidth: 10,
      editable: false,
      flex: 1,
    },
    {
      field: "reviewer",
      headerName: "Reviewer",
      minWidth: 100,
      flex: 1,
      editable: false,
    },
    {
      field: "approval_status",
      headerName: "Status",
      minWidth: 80,
      editable: false,
      flex: 1,
      renderCell: (param) => {
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
              gap: "10px",
            }}
          >
            {param.row.approval_status === "APPROVED" ? (
              <span className="approve">Approved</span>
            ) : param.row.approval_status === "REJECTED" ? (
              <span className="reject">Rejected</span>
            ) : (
              <></>
            )}
          </div>
        );
      },
    },
    {
      field: "payment_mode",
      headerName: "Payment Mode",
      minWidth: 150,
      editable: true,
      flex: 1,
      renderCell: (params) => {
        const currentMode = params.row.payment_mode ?? "";
        const isPending = params.row.payment_mode === "PENDING";

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center", // vertically centers the Select
              justifyContent: "center",
              height: "100%", // fill the cell height
            }}
          >
            <FormControl sx={{ m: 0, minWidth: 100 }} size="small">
              <Select
                value={paymentMode || currentMode}
                onChange={(e) => handleChange(e, params.row.slug)}
                displayEmpty
                inputProps={{ "aria-label": "Without label" }}
                className="custom-selectfield"
                disabled={!isPending}
                sx={{
                  paddingY: "2px", // equal top & bottom padding
                  paddingX: "8px", // horizontal padding
                  fontSize: "0.875rem",
                }}
              >
                <MenuItem value="PENDING">
                  <em>PENDING</em>
                </MenuItem>
                <MenuItem value="CARD">CARD</MenuItem>
                <MenuItem value="CASH">CASH</MenuItem>
                <MenuItem value="UPI">UPI</MenuItem>
                <MenuItem value="BANK_TRANSFER">BANK_TRANSFER</MenuItem>
              </Select>
            </FormControl>
          </div>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.8,
      minWidth: 150,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
              gap: "5px",
            }}
          >
            {flags.isAdmin || flags.isDirector ? (
              <>
                <Tooltip title="Approve">
                  <Button
                    size="small"
                    className={`approval_btn ${params.row.approval_status === "APPROVED" ? "APPROVED" : ""}`}
                    disabled={params.row.approval_status !== "PENDING"}
                    onClick={() => {
                      handleInvoiceAction("approve", params.row.slug);
                    }}
                    sx={{ marginRight: "5px" }}
                  >
                    <DoneOutlinedIcon sx={{ fontSize: "13px" }} />
                  </Button>
                </Tooltip>
                <Tooltip title="Reject">
                  <Button
                    size="small"
                    className={`approval_btn ${params.row.approval_status === "REJECTED" ? "REJECTED" : ""}`}
                    disabled={params.row.approval_status !== "PENDING"}
                    onClick={() => {
                      handleInvoiceAction("reject", params.row.slug);
                    }}
                    sx={{ marginRight: "5px" }}
                  >
                    <ClearOutlinedIcon sx={{ fontSize: "13px" }} />
                  </Button>
                </Tooltip>
              </>
            ) : (
              <></>
            )}
            <Tooltip title="Preview">
              <RemoveRedEyeOutlinedIcon
                sx={{
                  ...editPatientButton,
                }}
                onClick={() => {
                  handlePreview(params.row.slug);
                }}
              />
            </Tooltip>
            {params.row.approval_status === "APPROVED" && (
              <Tooltip title="Send Mail">
                <span>
                  <IconButton
                    disabled={params.row.mail_sent} // disables if mail already sent
                    onClick={() => {
                      setSelectedPatientId(params.row.patient_id);
                      setSelectedSlug(params.row.slug);
                      hanldleSendMail();
                    }}
                    sx={{
                      p: 0, // removes all padding
                      m: 0, // margin shorthand
                      width: "24px",
                      height: "24px",
                    }}
                  >
                    <ForwardToInboxOutlinedIcon
                      sx={{
                        ...printInvoiceButton,
                        color: params.row.mail_sent ? "green" : "red",
                      }}
                    />
                    <ClearOutlinedIcon sx={{ fontSize: "13px" }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}         
          </div>
        );
      },
    },
  ];
  const hanldleSendMail = () => {
    handleOpenDialog();
  };

  const handlePreview = async (slug) => {
    const url = APP_URL + `/invoice?slug=${slug}/`;
    window.open(url, "_blank");
  };

  const handleInvoiceAction = async (action, slug) => {
    try {
      const response = await api.post(
        `app/invoice_management/${slug}/action/`,
        { action: action },
      );
      if (response?.status === 200) {
        setSnackbarOpen({
          status: true,
          message:
            action === "approve"
              ? "Invoice Accepted Successfully!"
              : "Invoice Rejected Successfully",
        });
      }
    } catch (error) {
      setSnackbarOpen({
        severity: error,
        status: true,
        message: "Something went wrong! pleas try again",
      });
    }
    getInvoiceDetails();
  };

  const filteredRows = rows?.filter((row) => {
    const matchesSearch = Object.values(row).some(
      (val) =>
        val && val.toString().toLowerCase().includes(searchQuery.toLowerCase()),
    );

    let matchesDate = true;
    if (selectedDate) {
      matchesDate =
        row.year === selectedDate.year() &&
        row.month === selectedDate.month() + 1; // dayjs months are 0-based
    }

    return matchesSearch && matchesDate;
  });

  const hanldleAgreeSendMail = async () => {
    const response = await api.post("app/patient/email_invoice/", {
      patient_id: selectedPatientId,
      slug: selectedSlug,
    });
    getInvoiceDetails();
  };

  return (
    <>
      <Box className="tabcontent_box">
        <Grid
          container
          spacing={2}
          className="patientTable-btnstack"
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
        >
          <Grid size={6}></Grid>
          <Grid size={2} sx={{ textAlign: "right" }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                views={["year", "month"]}
                label="Filter Period"
                value={selectedDate}
                onChange={(newValue) => setSelectedDate(newValue)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
                slots={{
                  openPickerIcon: CalendarMonthOutlinedIcon,
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid size={3} sx={{ textAlign: "right" }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search ..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Grid>
        </Grid>

        <DataGrid
          sx={{
            fontFamily: "Nunito, sans-serif",
            // Cell (row content) font size
            "& .MuiDataGrid-cell": {
              fontSize: "12px",
            },

            // Header font size
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: "13px",
              fontWeight: "bold",
            },
          }}
          rows={filteredRows}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          pageSizeOptions={[5]}
          disableRowSelectionOnClick
          getRowId={(row) => row.slug}
        />
      </Box>
      <ConfirmDialog
        open={open}
        title="Confirm Mail Sending"
        description="Are you sure you want to send this mail? This action cannot be undone."
        agreeText="Send"
        disagreeText="Cancel"
        onClose={handleCloseDialog}
        onAgree={hanldleAgreeSendMail}
      />
      <ReusableSnackbar
        open={snackbarOpen.status}
        message={snackbarOpen.message}
        onClose={handleSnackClose}
        severity={snackbarOpen.severity}
      />
    </>
  );
}
